import axios from "axios";
import type { OwnerInvitation, TeamRole } from "./types";
import type { StoredScope } from "../organisation/access-model";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export interface DeveloperApiKeyRecord {
  api_key_id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

export interface DeveloperApiKeyList {
  api_keys: DeveloperApiKeyRecord[];
  active_count: number;
  max_active_keys: number;
}

export interface GeneratedDeveloperApiKey {
  api_key: string;
  api_key_id: string;
  name: string;
  created_at: string;
  last_used_at: string | null;
}

interface ApiEnvelope<T> {
  data: T;
}

const authHeaders = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export const getDeveloperApiKeys = async (token: string): Promise<DeveloperApiKeyList> => {
  const res = await axios.get<ApiEnvelope<DeveloperApiKeyList>>(
    `${API_URL}/api/v1/public/api-key`,
    authHeaders(token),
  );
  return res.data.data;
};

export const createDeveloperApiKey = async (token: string): Promise<GeneratedDeveloperApiKey> => {
  const res = await axios.post<ApiEnvelope<GeneratedDeveloperApiKey>>(
    `${API_URL}/api/v1/public/api-key`,
    {},
    authHeaders(token),
  );
  return res.data.data;
};

export const deleteDeveloperApiKey = async (apiKeyId: string, token: string): Promise<void> => {
  await axios.delete(
    `${API_URL}/api/v1/public/api-key/${encodeURIComponent(apiKeyId)}`,
    authHeaders(token),
  );
};

export const getOrganizationMembers = async (orgId: string, token: string) => {
  const res = await axios.get(`${API_URL}/api/v2/organizations/${orgId}/members`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

export const getAccountMembers = async (accountId: string, token: string) => {
  const res = await axios.get(`${API_URL}/api/v2/accounts/${accountId}/members`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

export const inviteOrganizationMember = async (
  orgId: string,
  email: string,
  role: TeamRole,
  token: string,
  // Only the org_invitations form passes this; the legacy invite body is unchanged.
  access?: { permissions: string[]; resource_scope: StoredScope | null },
) => {
  const res = await axios.post(`${API_URL}/api/v2/organizations/${orgId}/invitations`, {
    email,
    role_id: role,
    permissions: access?.permissions ?? [],
    ...(access ? { resource_scope: access.resource_scope } : {}),
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

export const updateMemberRole = async (orgId: string, userId: string, role: TeamRole, token: string) => {
  const res = await axios.patch(`${API_URL}/api/v2/organizations/${orgId}/members/${userId}`, {
    role_id: role
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

export const updateInvitationAccess = async (
  orgId: string,
  invitationId: string,
  changes: { role_id?: TeamRole; permissions?: string[] },
  token: string,
) => {
  const res = await axios.patch(
    `${API_URL}/api/v2/organizations/${orgId}/invitations/${invitationId}`,
    changes,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

const orgPath = (orgId: string, rest: string) =>
  `${API_URL}/api/v2/organizations/${encodeURIComponent(orgId)}${rest}`;

export const listOrganizationInvitations = async (orgId: string, token: string): Promise<OwnerInvitation[]> => {
  const res = await axios.get(orgPath(orgId, "/invitations"), authHeaders(token));
  return res.data;
};

export const resendOrganizationInvitation = async (
  orgId: string, invitationId: string, token: string,
): Promise<OwnerInvitation> => {
  const res = await axios.post(
    orgPath(orgId, `/invitations/${encodeURIComponent(invitationId)}/resend`), {}, authHeaders(token),
  );
  return res.data;
};

export const revokeOrganizationInvitation = async (
  orgId: string, invitationId: string, token: string,
): Promise<OwnerInvitation> => {
  const res = await axios.post(
    orgPath(orgId, `/invitations/${encodeURIComponent(invitationId)}/revoke`), {}, authHeaders(token),
  );
  return res.data;
};

export interface MemberAccessWrite {
  role_id: TeamRole;
  permissions: string[];
  /** null leaves the stored scope untouched. */
  resource_scope: StoredScope | null;
  expected_version: number | null;
}

/** member_grants: one versioned write replacing the role PATCH and the permissions PATCH. */
export const setMemberAccess = async (orgId: string, userId: string, access: MemberAccessWrite, token: string) => {
  const res = await axios.put(
    orgPath(orgId, `/members/${encodeURIComponent(userId)}/access`), access, authHeaders(token),
  );
  return res.data;
};

export const setMemberStatus = async (
  orgId: string, userId: string, status: "active" | "suspended", token: string,
) => {
  const res = await axios.patch(
    orgPath(orgId, `/members/${encodeURIComponent(userId)}/status`), { status }, authHeaders(token),
  );
  return res.data;
};

export const removeOrganizationMember = async (orgId: string, userId: string, token: string) => {
  const res = await axios.delete(`${API_URL}/api/v2/organizations/${orgId}/members/${userId}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

export const updateMemberPermissions = async (accId: string, userId: string, permissions: string[], token: string) => {
  // Try to update account member permissions
  const res = await axios.patch(`${API_URL}/api/v2/accounts/${accId}/members/${userId}/permissions`, {
    permissions
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

export const updateMemberVoiceAccess = async (
  orgId: string,
  userId: string,
  allowedVoices: string[],
  token: string,
) => {
  const res = await axios.patch(
    `${API_URL}/api/v2/organizations/${orgId}/members/${userId}/voices`,
    { allowed_voices: allowedVoices },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

export const updateMemberWhatsAppAccess = async (
  orgId: string,
  userId: string,
  allowedInstances: string[],
  token: string,
) => {
  const res = await axios.patch(
    `${API_URL}/api/v2/organizations/${orgId}/members/${userId}/whatsapp-instances`,
    { allowed_instances: allowedInstances },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return res.data;
};

export const getOrganizationVoices = async (orgId: string, token: string) => {
  const res = await axios.get(`${API_URL}/api/v2/organizations/${orgId}/voices`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data as { allowed_voices: string[] | null; voices: { local_voice_name: string; gender: string }[] };
};

export const changePassword = async (currentPassword: string, newPassword: string, token: string) => {
  const res = await axios.post(`${API_URL}/api/v2/auth/change-password`, {
    current_password: currentPassword,
    new_password: newPassword,
  }, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data;
};

// ── Secondary (alert) emails ───────────────────────────────────────────────

interface SecondaryEmailsResponse {
  secondary_emails: string[];
}

export const getSecondaryEmails = async (token: string): Promise<SecondaryEmailsResponse> => {
  const res = await axios.get(`${API_URL}/api/v2/auth/secondary-emails`, authHeaders(token));
  return res.data;
};

export const addSecondaryEmail = async (email: string, token: string): Promise<SecondaryEmailsResponse> => {
  const res = await axios.post(`${API_URL}/api/v2/auth/secondary-emails`, { email }, authHeaders(token));
  return res.data;
};

export const updateSecondaryEmail = async (
  oldEmail: string,
  newEmail: string,
  token: string,
): Promise<SecondaryEmailsResponse> => {
  const res = await axios.put(
    `${API_URL}/api/v2/auth/secondary-emails`,
    { old_email: oldEmail, new_email: newEmail },
    authHeaders(token),
  );
  return res.data;
};

export const deleteSecondaryEmail = async (email: string, token: string): Promise<SecondaryEmailsResponse> => {
  const res = await axios.delete(`${API_URL}/api/v2/auth/secondary-emails`, {
    ...authHeaders(token),
    data: { email },
  });
  return res.data;
};

// ── User Preferences ──────────────────────────────────────────────────────────

export interface UserPreferencesResponse {
  success: boolean;
  timezone: string;
  vpn_detected?: boolean;
  network_info?: {
    is_vpn: boolean;
    ip: string;
    provider: string;
    region: string;
  };
}

export interface NetworkCheckResponse {
  vpn_detected: boolean;
  ip: string;
  provider: string;
  region: string;
}

export const checkNetworkConnection = async (token?: string): Promise<NetworkCheckResponse> => {
  const headers = token ? authHeaders(token).headers : {};
  const res = await axios.get(`${API_URL}/api/v2/auth/network-check`, { headers });
  return res.data;
};

export const getUserPreferences = async (token: string): Promise<UserPreferencesResponse> => {
  const res = await axios.get(`${API_URL}/api/v2/auth/preferences`, authHeaders(token));
  return res.data;
};

export const updateUserPreferences = async (
  preferences: { timezone: string },
  token: string,
): Promise<UserPreferencesResponse> => {
  const res = await axios.patch(
    `${API_URL}/api/v2/auth/preferences`,
    preferences,
    authHeaders(token),
  );
  return res.data;
};
